import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * ONE card for everything the four old cards said (owner, 2026-09-02: "limited
 * amount of data taking up so much space"). General Details, Address
 * Information, Property Details and Additional Information each held three or
 * four short facts inside a half-empty box; together they were most of a
 * screen. The same facts sit here in a single dense grid.
 */
function PropertyInfoCard({ property }) {
    const [copied, setCopied] = useState(false);
    const [billedToLabel, setBilledToLabel] = useState(null);

    useEffect(() => {
        setBilledToLabel(null);
        if (!property?.billing_profile_id || !property?.client_id) return undefined;
        let cancelled = false;
        axios.get(`/api/clients/${property.client_id}/billing-profiles`)
            .then((res) => {
                if (cancelled) return;
                const match = (res.data?.profiles || []).find((p) => p.id === property.billing_profile_id);
                setBilledToLabel(match ? (match.label || match.legal_name) : null);
            })
            .catch(() => { /* row simply not shown */ });
        return () => { cancelled = true; };
    }, [property?.billing_profile_id, property?.client_id]);

    const dt = (iso) => {
        const d = DateTime.fromISO(iso, { zone: 'Asia/Dubai' });
        return d.isValid ? d.toFormat('dd LLL yyyy — HH:mm') : '—';
    };

    const copyMaps = () => {
        navigator.clipboard.writeText(property.google_maps_link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const beds = `${property.double_beds || 0} double · ${property.single_beds || 0} single`
        + (Number(property.super_king_beds) > 0 ? ` · ${property.super_king_beds} super king` : '');

    const Item = ({ label, children }) => (
        <div className="dp-item">
            <span className="dp-label">{label}</span>
            <span className="dp-value">{children ?? '—'}</span>
        </div>
    );

    return (
        <div className="dp-card">
            <h2>Property Information</h2>
            <div className="dp-grid">
                <Item label="Client">
                    <a href={`/clients/${property.client_id}`} className="ld-link">{property.client_display_name}</a>
                </Item>
                {billedToLabel && <Item label="Billed to">{billedToLabel}</Item>}
                <Item label="Building">{property.address_line_1 || '—'}</Item>
                <Item label="Unit">{property.unit_number || '—'}</Item>
                <Item label="Beds">{beds}</Item>
                {/* Price: ALWAYS on the top row, and is_custom_price is the
                    only truth — a custom rate can legitimately be 0, so the
                    amount never decides the label (owner, 2026-09-02). Just
                    the AED figure, never the item name. */}
                {property.is_custom_price ? (
                    <Item label="Custom price">AED {property.custom_price ?? 0}</Item>
                ) : (
                    <Item label="Default price">
                        {property.item_default_price != null ? `AED ${property.item_default_price}` : '—'}
                    </Item>
                )}
                <Item label="Last booking">
                    {property.last_booking_date
                        ? <a href={`/bookings/${property.last_booking_id}`} className="ld-link">{dt(property.last_booking_date)}</a>
                        : 'None yet'}
                </Item>
                <Item label="Next booking">
                    {property.next_booking_date
                        ? <a href={`/bookings/${property.next_booking_id}`} className="ld-link">{dt(property.next_booking_date)}</a>
                        : 'None scheduled'}
                </Item>
                <Item label="Property access">{property.property_access || '—'}</Item>
                <Item label="WiFi name">{property.wifi_name || '—'}</Item>
                <Item label="WiFi password">{property.wifi_password || '—'}</Item>
                {property.google_maps_link && (
                    <div className="dp-item">
                        <span className="dp-label">Location</span>
                        <span className="dp-value">
                            <span className="google-maps-link-row">
                                <a href={property.google_maps_link} target="_blank" rel="noopener noreferrer" className="dp-mapsBtn">
                                    Open in Google Maps ↗
                                </a>
                                <button className="icon-button" onClick={copyMaps} title="Copy the link">
                                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                                </button>
                            </span>
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PropertyInfoCard;
